import { useCallback, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOAuthPopup } from "./useOAuthPopup";
import {
  beginSlackOAuthRedirect,
  disconnectSlack as disconnectSlackApi,
  fetchSlackAuthorizeUrl,
  getSlackConnectionStatus,
} from "../services/notifications/slackApi";
import { queryKeys } from "../services/queryKeys";

interface UseSlackConnectionResult {
  /** True once the status query has resolved and Slack is connected. */
  isConnected: boolean;
  /** True while the initial status check is in flight (first render). */
  isStatusLoading: boolean;
  /** True while the OAuth popup is open, waiting on the user. */
  connecting: boolean;
  /** User-facing message from the last failed attempt, if any. */
  connectError: string | null;
  /** Opens "Add Slack" — returnTo defaults to the current route so
   * the backend can send the user back here once the OAuth flow
   * completes, regardless of where it was started from. */
  connectSlack: (returnTo?: string) => Promise<void>;
  /** True while a disconnect request is in flight. */
  disconnecting: boolean;
  /** Disconnects the current workspace so "Add Slack" reappears. */
  disconnectSlack: () => Promise<void>;
}

/**
 * Slack-connect status + entry point for the Share flow. Reuses the
 * `useOAuthPopup` engine shared with the Salesforce connector so the
 * popup/postMessage mechanics aren't duplicated — only the endpoint calls
 * differ.
 */
export const useSlackConnection = (): UseSlackConnectionResult => {
  const queryClient = useQueryClient();
  const [connectError, setConnectError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);
  // Ref, not state: `connectSlack` sets this and calls `startOAuth()`
  // synchronously right after — state wouldn't re-render in time for the
  // `fetchAuthorizeUrl` closure below to see the new value before use.
  const returnToRef = useRef<string | undefined>(undefined);

  const {
    data: connection,
    isLoading: isStatusLoading,
  } = useQuery({
    queryKey: queryKeys.notifications.slackConnection(),
    queryFn: getSlackConnectionStatus,
    staleTime: 30_000,
  });

  const isConnected = connection?.status === "connected";

  const { authorizing, startOAuth } = useOAuthPopup({
    fetchAuthorizeUrl: () => fetchSlackAuthorizeUrl(returnToRef.current),
    beginFullPageRedirect: async () => beginSlackOAuthRedirect(returnToRef.current),
    onConnected: () => {
      setConnectError(null);
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.slackConnection() });
    },
    onError: (message) => setConnectError(message),
    // Safety net: re-check status whenever the popup closes, regardless of
    // whether the postMessage success signal actually arrived. This is what
    // fixes the "backend connected but UI still says not connected until a
    // manual refresh" symptom — the popup closing is a reliable signal even
    // when the message race isn't.
    onPopupClosed: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.slackConnection() });
    },
  });

  const connectSlack = useCallback(
    async (returnTo?: string) => {
      setConnectError(null);
      returnToRef.current = returnTo ?? `${window.location.pathname}${window.location.search}`;
      await startOAuth();
    },
    [startOAuth],
  );

  const disconnectSlack = useCallback(async () => {
    setDisconnecting(true);
    try {
      await disconnectSlackApi();
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.slackConnection() });
    } finally {
      setDisconnecting(false);
    }
  }, [queryClient]);

  return {
    isConnected,
    isStatusLoading,
    connecting: authorizing,
    connectError,
    connectSlack,
    disconnecting,
    disconnectSlack,
  };
};

export default useSlackConnection;
